import React from 'react';

import {apiClient} from 'common/util/api-client';
import {DeepPartial} from 'common/util/typescript';

import {Experiment} from '../types';

export type UseExperimentsApiProps = {
  projectId: number;
  workspaceId: number;
};

// Request types
type GetExperimentRequest = {id: string};
export type CreateExperimentRequest = DeepPartial<Experiment>;
type DuplicateExperimentRequest = {id: string};
export type UpdateExperimentRequest = DeepPartial<Experiment> & {id: string};
type DeleteExperimentRequest = {id: string};
type LaunchExperimentRequest = {id: string};
type ForceConcludeExperimentRequest = {id: string};
type DecideExperimentRequest = {id: string; success: boolean; message: string};

// Response types
type GetExperimentResponse = Experiment;
type ListExperimentsResponse = Experiment[];
type DeleteExperimentResponse = {id: string};

export function useExperimentsApi({projectId, workspaceId}: UseExperimentsApiProps) {
  const basePath = React.useMemo(() => {
    const parentEntityPath = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
    return `/api/app/${parentEntityPath}/experiments`;
  }, [workspaceId, projectId]);

  const getExperiment = React.useCallback(
    ({id}: GetExperimentRequest) => {
      return apiClient.get<GetExperimentResponse>(`${basePath}/${id}`);
    },
    [basePath],
  );

  const listExperiments = React.useCallback(() => {
    return apiClient.get<ListExperimentsResponse>(basePath);
  }, [basePath]);

  const createExperiment = React.useCallback(
    (experiment: CreateExperimentRequest) => {
      return apiClient.post<Experiment>(basePath, experiment);
    },
    [basePath],
  );

  const duplicateExperiment = React.useCallback(
    ({id}: DuplicateExperimentRequest) => {
      return apiClient.post<Experiment>(`${basePath}/${id}/duplicate`, {});
    },
    [basePath],
  );

  const updateExperiment = React.useCallback(
    ({id, ...experiment}: UpdateExperimentRequest) => {
      return apiClient.patch<Experiment>(`${basePath}/${id}`, experiment);
    },
    [basePath],
  );

  const deleteExperiment = React.useCallback(
    ({id}: DeleteExperimentRequest) => {
      return apiClient.delete<DeleteExperimentResponse>(`${basePath}/${id}`);
    },
    [basePath],
  );

  const launchExperiment = React.useCallback(
    ({id}: LaunchExperimentRequest) => {
      return apiClient.put<Experiment>(`${basePath}/${id}/launch`, {});
    },
    [basePath],
  );

  const forceConcludeExperiment = React.useCallback(
    ({id}: ForceConcludeExperimentRequest) => {
      return apiClient.put<Experiment>(`${basePath}/${id}/force_conclude`, {});
    },
    [basePath],
  );

  const decideExperiment = React.useCallback(
    ({id, success, message}: DecideExperimentRequest) => {
      return apiClient.patch<Experiment>(`${basePath}/${id}/decide`, {
        success,
        message,
      });
    },
    [basePath],
  );

  return {
    getExperiment,
    listExperiments,
    createExperiment,
    duplicateExperiment,
    updateExperiment,
    deleteExperiment,
    launchExperiment,
    forceConcludeExperiment,
    decideExperiment,
  };
}
